import type { ReactNode } from 'react'

import { NotificationButtonStyled } from './styles/NotificationButtonStyled'
import { NotificationContainerStyled } from './styles/NotificationContainerStyled'
import { NotificationIconStyled } from './styles/NotificationIconStyled'
import { NotificationTextStyled } from './styles/NotificationTextStyled'
import { NotificationTitleStyled } from './styles/NotificationTitleStyled'
import { type NotificationType } from './types'
import { Icon, type IconTypes } from '../Icon'
import { Space } from '../Space'

export interface INotificationProps {
  /**
   * Optional body copy shown under the title. Keep it to three lines or fewer
   * and end it with a period, except when it ends in an inline action link.
   */
  children?: ReactNode
  /** Visual treatment and icon: `success`, `error`, `info`, or `warning`. */
  type: NotificationType
  /**
   * The one-line heading. Start it with a noun + verb (e.g. *Payment
   * submitted*, *Download failed*) since a toast is often far from the element
   * it refers to.
   */
  title: ReactNode
  /** Called when the dismiss (×) button is pressed. */
  onDismiss?: () => void
}

const icons = {
  error: 'close',
  info: 'notification',
  success: 'approval',
  warning: 'attention',
} as Record<NotificationType, IconTypes>

/**
 * A single notification card — a type icon, a title, optional body copy, and a
 * dismiss button. It provides brief, non-disruptive feedback on an action the
 * user took or a system status update.
 *
 * In the app you rarely render this directly. Fire it as a toast with the
 * `toast` helper from `@webapp/components/Notification`, which mounts it in the
 * app-wide toast container and auto-dismisses it after 5 seconds.
 */
export const Notification = ({
  type,
  title,
  children,
  onDismiss,
}: INotificationProps) => (
  <NotificationContainerStyled>
    <NotificationIconStyled type={type}>
      <Icon size={16} icon={icons[type]} />
    </NotificationIconStyled>
    <Space size='xsmall'>
      <Space direction='horizontal' align='center' justify='space-between'>
        <NotificationTitleStyled>{title}</NotificationTitleStyled>
        <NotificationButtonStyled onClick={onDismiss}>
          <Icon icon='times' size={11} />
        </NotificationButtonStyled>
      </Space>
      <NotificationTextStyled>{children}</NotificationTextStyled>
    </Space>
  </NotificationContainerStyled>
)
